import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { Snackbar, Alert } from "@mui/material";
import "./jobs.scss";
import { useDispatch, useSelector } from "react-redux";
import { getAction, getAutomation, getAutomationApp, getTrigger, postAutomationNodes, postAutomationLink, createAutomations, updateAutomation, triggerAutomationRun  } from "../../Redux/ActionCreators/automationAction";
import { useParams } from "react-router-dom";
import ToggleSwitch from "../../utils/toggleSwitch";

const Automation = () => {
  const dispatch = useDispatch();
  const { jobId } = useParams();

  const [showModal, setShowModal] = useState(false);
  const [loadingTrigger, setLoadingTrigger] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [conditionField, setConditionField] = useState("");
  const [conditionValue, setConditionValue] = useState("");
  const [conditionComparator, setConditionComparator] = useState("");
  const [savingRule, setSavingRule] = useState(false);
  const [loadingAutomations, setLoadingAutomations] = useState(true);

  const comparatorMap = {
    gte: "greater than or equal",
    lte: "less than or equal",
    eq: "equals",
    ne: "not equal",
    lt: "less than",
    gt: "greater than"
  };

  const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });

  const [form, setForm] = useState({
    trigger: "",
    action: "",
  });

  const automationApp = useSelector((state) => state.automation?.automationApp || null);
  const automationDetails = useSelector((state) => state.automation?.automationDetails || null);
  const triggerList = useSelector((state) => state.automation?.triggerDetails || []);
  const actionList = useSelector((state) => state.automation?.actionDetails || []);
  const createAutomation = useSelector((state) => state.automation?.createAutomation || []);

  const tenantId = localStorage.getItem("tenentId");

  useEffect(() => {
    const initialFetch = async () => {
      setLoadingAutomations(true);
      const appPayload = { page: 1, page_size: 20 };
      await dispatch(getAutomationApp(appPayload));
      await dispatch(getTrigger())
      await dispatch(getAction());
      setLoadingAutomations(false);
    }
    initialFetch();
  }, [dispatch]);

  useEffect(() => {
    if (!automationApp || automationApp.length === 0) return;

    const payload = {
      job_id: jobId,
      tenant_id: tenantId,
      is_active: true,
      app_id: automationApp[0].id
    };

    setLoadingAutomations(true);
    dispatch(getAutomation(payload)).finally(() => setLoadingAutomations(false));

  }, [automationApp, jobId, tenantId]);


  const tableData = React.useMemo(() => {
    if (!automationDetails || !triggerList.length || !actionList.length)
      return [];

    return automationDetails.map(a => {
      const triggerNode = a.nodes?.find(n => n.type === "trigger");
      const actionNode = a.nodes?.find(n => n.type === "action");

      const triggerName = triggerList.find(t => t.id === triggerNode?.reference_id)?.name || "-";
      const actionName = actionList.find(a => a.id === actionNode?.reference_id)?.name || "-";

      const link = a.links?.[0];
      const cond = link?.link_condition;

      let conditionText = "-";

      if (cond) {
        const opText = comparatorMap[cond.op] || cond.op;
        conditionText = `${opText} score ${cond.value}%`;
      }

      return {
        id: a.id,
        trigger: triggerName,
        action: actionName,
        active: a.is_active,
        condition: conditionText
      };
    });
  }, [automationDetails, triggerList, actionList]);

  const getSelectedTriggerId = () => {
    const t = triggerList.find(tr => tr.name === form.trigger);
    return t?.id || null;
  };

  const getSelectedActionId = () => {
    const a = actionList.find(ac => ac.name === form.action);
    return a?.id || null;
  };

  const handleSave = async () => {
    if (!form.trigger) {
      return setSnack({ open: true, message: "Please select a Trigger", severity: "error" });
    }
    if (!conditionField || !conditionValue || !conditionComparator) {
      return setSnack({ open: true, message: "Please complete the Condition", severity: "error" });
    }
    if (!form.action) {
      return setSnack({ open: true, message: "Please select an Action", severity: "error" });
    }

    const automationResp = await dispatch(createAutomations(createAutomationPayload));
    if (!automationResp?.id) {
      return setSnack({
        open: true,
        message: "Failed to create automation",
        severity: "error"
      });
    }

    // Use ID returned from reducer/store  
    const automationId = automationResp.id;

    const triggerId = getSelectedTriggerId();
    const actionId = getSelectedActionId();

    if (!triggerId) {
      return setSnack({ open: true, message: "Invalid trigger selection", severity: "error" });
    }
    if (!actionId) {
      return setSnack({ open: true, message: "Invalid action selection", severity: "error" });
    }

    try {
      setSavingRule(true);
      // 1️ CREATE TRIGGER NODE
      const triggerPayload = {
        type: "trigger",
        reference_id: triggerId,
        config: { },
        // config: { field: conditionField, comparator: conditionComparator, value: conditionValue },
        position_x: 0,
        position_y: 0
      };

      const triggerNodeResp = await dispatch(postAutomationNodes(automationId, triggerPayload));

       if (!triggerNodeResp?.id) throw new Error("Failed to create trigger node");

      // 2️ CREATE ACTION NODE
      const actionPayload = {
        type: "action",
        reference_id: actionId,
        config: { },
        position_x: 0,
        position_y: 0
      };

      const actionNodeResp = await dispatch(postAutomationNodes(automationId, actionPayload));
      if (!actionNodeResp?.id) throw new Error("Failed to create action node");

      const trigger = triggerList.find(t => t.id === triggerId);
      const backendField = trigger?.output_schema?.field;

      // 3️ CREATE LINK
      const linkPayload = {
        from_node_id: triggerNodeResp?.id,
        to_node_id: actionNodeResp?.id,
        link_condition: { 
          field: backendField,
          op: conditionComparator,
          value: Number(conditionValue)
         }
      };

      const linkResp = await dispatch(postAutomationLink(automationId, linkPayload));

      await dispatch(triggerAutomationRun(automationId, "IN_PROGRESS"));

      if (!linkResp?.id) throw new Error("Failed to create node link");

      // 4️ SUCCESS UI UPDATES
      setSnack({ open: true, message: "Automation created successfully", severity: "success" });

      setShowModal(false);

      // Reset fields
      setForm({ trigger: "", action: "" });
      setConditionField("");
      setConditionValue("");
      setConditionComparator("");

      // Refresh automation list
      await dispatch( getAutomation({job_id: jobId, tenant_id: tenantId, is_active: true, app_id: automationApp?.[0]?.id})
      );

    } catch (error) {
      setSnack({ open: true, message: error.message || "Failed to create rule", severity: "error" });
    }

    setSavingRule(false);
  };

  const ruleCount = automationDetails?.length || 0;
  const autoGeneratedName = `automation rule ${ruleCount + 1}`;
  const isTableReady = automationDetails !== null && triggerList.length > 0 && actionList.length > 0 && !loadingAutomations;

  const createAutomationPayload = {
    name: autoGeneratedName,
    description: '',
    app_id: automationApp?.[0]?.id,
    is_template: false,
    job_id: jobId,
    tenant_id: Number(tenantId),
  }

  const handleCreateRule = async () => {
    setShowModal(true);
  }

  const handleTriggerChange = async (e) => {
    setForm({trigger: e.target.value, condition: "", action: ""});
    setConditionField("");
    setConditionValue("");
    setConditionComparator("");
  }

  const getConditionFields = () => {
    if (!form.trigger) return [];
    const trigger = triggerList.find(t => t.name === form.trigger);
    if (!trigger) return [];
    const fieldName = trigger.output_schema?.field; 
    return [fieldName];
  };

  const toggleActive = async (id) => {
    try {
      const selectedRow = automationDetails.find(item => item.id === id);
      const updatedStatus = !selectedRow.is_active;

      const payload = {
        is_active: updatedStatus,
      };

      // 1. Update automation
      const resp = await dispatch(updateAutomation(id, payload));

      if (updatedStatus === true) {
        await dispatch(triggerAutomationRun(id, "IN_PROGRESS"));
      } else {
        await dispatch(triggerAutomationRun(id, "COMPLETED"));
      }

      // 2. Refresh automation list to update UI
      const appId = automationApp?.[0]?.id;

      await dispatch(getAutomation({
        job_id: jobId,
        tenant_id: tenantId,
        is_active: true,
        app_id: appId
      }));

    } catch (error) {
      console.error("Error updating automation:", error);
    }
  };
  

  return (
    <div className="job-automation">
      <div className="top-navigation">
        <h4>Rules</h4>
          <button className="create-job" onClick={handleCreateRule}>
            Create Rules
          </button>
      </div>

      <div className="table-content">
        <Table responsive className="job-table">
          <thead>
            <tr>
              <th>Trigger</th>
              <th>Condition</th>
              <th>Action</th>
              <th className="text-center">Active</th>
            </tr>
          </thead>
          <tbody>
            {!isTableReady ? (
              <tr>
                <td colSpan="4" style={{ textAlign: "center" }}>
                  <div className="loading-overlay">
                    <div className="spinner-border"></div>
                  </div>
                </td>
              </tr>
            ) : tableData.length === 0 ? (
              <tr>
                <td colSpan="4">No rules has been added for this job.</td>
              </tr>
            ) : (
              tableData.map((item) => (
                <tr key={item.id}>
                  <td>{item.trigger}</td>
                  <td>{item.condition}</td>
                  <td>{item.action}</td>
                  <td className="text-center">
                    <ToggleSwitch
                      checked={item.active}
                      onChange={() => toggleActive(item.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="upload-modal automation-modal">
          <div className="modal-content">
            <div className="modal-head">
              <div></div>
              <h3>Create Rules</h3>
              <img
                src="/images/close-icon.svg"
                alt=""
                onClick={() => {setShowModal(false);setConditionField("");setConditionValue("");setConditionComparator("")}}
              />
            </div>
            <div className="modal-input">
              <div className="automation-dropdown">
                <label>Trigger *</label>
                <select value={form.trigger} onChange={(e) => handleTriggerChange(e)}>
                  <option value="">Select Trigger</option>
                  {triggerList.map(t => (
                    <option key={t.id} value={t.name}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="automation-dropdown">
                <label style={{textAlign:'center'}}>Condition *</label>
                <div className="d-flex gap-3">
                  <select
                    disabled={!form.trigger}
                    value={conditionField}
                    onChange={(e) => {
                      setConditionField(e.target.value);
                      setConditionValue("");
                      setConditionComparator("");
                    }}
                    style={{width:'100px'}}
                  >
                    <option value="">Select Field</option>
                    {getConditionFields().map((field, idx) => (
                      <option key={idx} value={field}>Score</option>
                    ))}
                  </select>

                  <input
                    type="number"
                    min="0"
                    max="100"
                    disabled={!conditionField}
                    value={conditionValue}
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "+") e.preventDefault();
                    }}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value < 0) value = 0;
                      if (value > 100) value = 100;
                      setConditionValue(value);
                    }}
                    placeholder="0 - 100"
                    style={{width:'100px'}}
                  />

                  <select
                    disabled={!conditionField || !conditionValue}
                    value={conditionComparator}
                    onChange={async (e) => {
                      setConditionComparator(e.target.value);
                      setLoadingAction(true);
                      await dispatch(getAction());
                      setLoadingAction(false);
                      setForm({ ...form, condition: "custom_condition" });
                    }}
                    style={{width:'100px'}}
                  >
                    <option value="">Select Comparator</option>
                    <option value="gte">≥ Greater than equal</option>
                    <option value="lte">≤ Less than equal</option>
                    <option value="eq">= Equal</option>
                    <option value="ne">≠ Not equal</option>
                    <option value="lt">&lt; Less than</option>
                    <option value="gt">&gt; Greater than</option>
                  </select>
                </div>
              </div>

              <div className="automation-dropdown">
                {!loadingAction ? (
                  <>
                    <label>Action *</label>
                    <select
                      disabled={!form.condition}
                      value={form.action}
                      onChange={(e) => setForm({ ...form, action: e.target.value })}
                    >
                      <option value="">Select Action</option>
                      {actionList.map(a => (
                        <option key={a.id} value={a.name}>{a.name}</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <div className="loading-overlay" style={{ height: "34px", width:'267px' }}>
                    <div className="spinner-border"></div>
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-center">
              <button className="upload-btn" disabled={savingRule} style={{ backgroundColor: savingRule ? "#ccc" : "" }} onClick={handleSave}>
                {savingRule ? "Saving..." : "Save Rules"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default Automation;